import React, { useEffect, useState } from "react";
import { api, formatFCFA, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Eye, CheckCircle2, XCircle, X, Trash2 } from "lucide-react";

const STATUS = {
  en_attente: { label: "En attente", className: "bg-yellow-900/40 text-yellow-300 border-yellow-500/30" },
  livre_notifie: { label: "Livré et notifié", className: "bg-green-900/40 text-green-300 border-green-500/30" },
  non_livre: { label: "Non livré", className: "bg-red-900/40 text-red-300 border-red-500/30" },
};

const SATISFACTION = {
  tres_satisfait: { label: "⭐ Très satisfait(e)", className: "text-[#B8860B]" },
  satisfait: { label: "🙂 Satisfait(e)", className: "text-green-300" },
  non_satisfait: { label: "😔 Non satisfait(e)", className: "text-red-300" },
};

const DEFAULT_MSG_LIVRE = `Bonjour Monsieur / Madame,

Nous vous remercions pour votre achat auprès de Refuge de la Foi.
Votre commande a bien été livrée.

Nous serions heureux de connaître votre niveau de satisfaction :

⭐ Très satisfait(e), je recommande
🙂 Satisfait(e)
😔 Non satisfait(e)

Merci pour votre confiance.`;

const DEFAULT_MSG_NON_LIVRE = `Bonjour Monsieur / Madame,

Nous sommes navrés de n'avoir pas pu effectuer votre livraison.
Nous vous invitons à visiter à nouveau notre site pour une nouvelle commande.

Nous prenons toutes les dispositions pour améliorer la prochaine expérience.

Merci pour votre compréhension.`;

function buildWaLink(phoneRaw, message) {
  const phone = (phoneRaw || "").replace(/[^\d]/g, "");
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePwd, setDeletePwd] = useState("");
  const [deleting, setDeleting] = useState(false);
  const { settings } = useSettings();

  const msgLivre = settings?.delivered_message || DEFAULT_MSG_LIVRE;
  const msgNonLivre = settings?.non_delivered_message || DEFAULT_MSG_NON_LIVRE;

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (order, status, message) => {
    try {
      const res = await api.patch(`/admin/orders/${order.id}/status?status=${status}`);
      toast.success("Statut mis à jour");
      if (message) {
        window.open(buildWaLink(order.phone, message), "_blank");
      }
      load();
      if (selected?.id === order.id) setSelected(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setSatisfaction = async (order, satisfaction) => {
    try {
      const res = await api.patch(`/admin/orders/${order.id}/satisfaction?satisfaction=${satisfaction}`);
      toast.success("Avis enregistré");
      load();
      if (selected?.id === order.id) setSelected(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markLivre = (o) => setStatus(o, "livre_notifie", msgLivre);
  const markNonLivre = (o) => setStatus(o, "non_livre", msgNonLivre);

  const confirmDelete = async () => {
    if (!deleteTarget || !deletePwd) {
      toast.error("Mot de passe requis");
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${deleteTarget.id}?password=${encodeURIComponent(deletePwd)}`);
      toast.success("Commande supprimée");
      setDeleteTarget(null);
      setDeletePwd("");
      if (selected?.id === deleteTarget.id) setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = filter ? orders.filter((o) => o.status === filter) : orders;

  return (
    <div>
      <h1 className="font-display text-3xl lg:text-4xl mb-2">Commandes</h1>
      <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mb-8">{orders.length} commande(s)</p>

      <div className="flex gap-2 flex-wrap mb-6">
        <button onClick={() => setFilter("")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${!filter ? "bg-[#B8860B] text-[#0A0A0A] border-[#B8860B]" : "border-black/10"}`}>
          Tout ({orders.length})
        </button>
        {Object.entries(STATUS).map(([k, s]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-orders-${k}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border ${filter === k ? "bg-[#B8860B] text-[#0A0A0A] border-[#B8860B]" : "border-black/10"}`}
          >
            {s.label} ({orders.filter((o) => o.status === k).length})
          </button>
        ))}
      </div>

      <div className="border border-black/5 bg-white overflow-x-auto" data-testid="admin-orders-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="border-b border-black/5 text-[10px] uppercase tracking-widest text-[#6B6B6B]">
            <tr>
              <th className="px-4 py-3 text-left">N°</th>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Total</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3 text-left">Avis</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="text-center py-12 text-[#6B6B6B]">Aucune commande.</td></tr>
            )}
            {filtered.map((o) => (
              <tr key={o.id} className="border-b border-black/5 hover:bg-[#B8860B]/[0.02]">
                <td className="px-4 py-3 text-[#B8860B] font-mono text-xs">{o.order_number}</td>
                <td className="px-4 py-3">
                  <div className="text-[#1A1A1A]">{o.customer_name}</div>
                  <div className="text-xs text-[#6B6B6B]">{o.phone}</div>
                </td>
                <td className="px-4 py-3 text-xs text-[#6B6B6B]">{new Date(o.created_at).toLocaleString("fr-FR")}</td>
                <td className="px-4 py-3 text-[#B8860B]">{formatFCFA(o.total)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-1 border text-[10px] uppercase tracking-widest ${STATUS[o.status]?.className}`}>
                    {STATUS[o.status]?.label || o.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">
                  {o.satisfaction ? (
                    <span className={SATISFACTION[o.satisfaction]?.className}>{SATISFACTION[o.satisfaction]?.label}</span>
                  ) : (
                    <span className="text-[#6B6B6B] italic">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => setSelected(o)} className="p-2 hover:text-[#B8860B]" data-testid={`view-order-${o.id}`} title="Voir détails">
                      <Eye size={14} />
                    </button>
                    {o.status !== "livre_notifie" && (
                      <button onClick={() => markLivre(o)} className="p-2 hover:text-green-400" title="Livré et notifié" data-testid={`mark-livre-${o.id}`}>
                        <CheckCircle2 size={14} />
                      </button>
                    )}
                    {o.status !== "non_livre" && (
                      <button onClick={() => markNonLivre(o)} className="p-2 hover:text-red-400" title="Non livré" data-testid={`mark-non-livre-${o.id}`}>
                        <XCircle size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => { setDeleteTarget(o); setDeletePwd(""); }}
                      className="p-2 hover:text-red-700"
                      title="Supprimer la commande"
                      data-testid={`delete-order-${o.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-6 py-10 overflow-y-auto" onClick={() => setSelected(null)} data-testid="order-detail-modal">
          <div className="bg-white border border-[#B8860B]/30 max-w-2xl w-full p-8 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-display text-2xl text-[#B8860B]">Commande {selected.order_number}</h3>
                <p className="text-xs text-[#6B6B6B] mt-1">{new Date(selected.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <button onClick={() => setSelected(null)}><X /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm mb-6">
              <div><div className="label-tiny">Nom</div><div>{selected.customer_name}</div></div>
              <div><div className="label-tiny">Téléphone</div><div>{selected.phone}</div></div>
              <div className="col-span-2"><div className="label-tiny">Adresse précise</div><div>{selected.address}</div></div>
              <div className="col-span-2"><div className="label-tiny">Commune</div><div>{selected.city} {selected.delivery_zone && <span className="text-[#6B6B6B]">({selected.delivery_zone})</span>}</div></div>
              {selected.remark && <div className="col-span-2"><div className="label-tiny">Remarque</div><div className="italic">{selected.remark}</div></div>}
            </div>

            <div className="label-tiny mb-2">Articles</div>
            <div className="border border-black/5 mb-4">
              {selected.items.map((it, i) => (
                <div key={i} className="flex justify-between p-3 border-b border-black/5 last:border-0 text-sm">
                  <div>{it.name}{it.variant_name ? ` (${it.variant_name})` : ""} <span className="text-[#6B6B6B]">x{it.quantity}</span></div>
                  <div className="text-[#B8860B]">{formatFCFA(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#6B6B6B]">Sous-total</span><span>{formatFCFA(selected.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#6B6B6B]">Livraison ({selected.delivery_zone})</span><span>{formatFCFA(selected.delivery_fee)}</span></div>
              <div className="flex justify-between font-display text-lg pt-2 border-t border-black/10 mt-2"><span>Total</span><span className="text-[#B8860B]">{formatFCFA(selected.total)}</span></div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                onClick={() => markLivre(selected)}
                className="btn-primary"
                disabled={selected.status === "livre_notifie"}
                data-testid="modal-mark-livre"
              >
                <CheckCircle2 size={14} /> Livré et notifié
              </button>
              <button
                onClick={() => markNonLivre(selected)}
                className="btn-primary !bg-red-700 !border-red-700 !text-white"
                disabled={selected.status === "non_livre"}
                data-testid="modal-mark-non-livre"
              >
                <XCircle size={14} /> Non livré
              </button>
            </div>

            <div className="mt-8 border-t border-black/5 pt-6">
              <div className="label-tiny mb-3">Avis client (réponse WhatsApp)</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(SATISFACTION).map(([k, s]) => (
                  <button
                    key={k}
                    onClick={() => setSatisfaction(selected, k)}
                    className={`px-3 py-2 text-xs border transition ${
                      selected.satisfaction === k
                        ? "bg-[#B8860B] text-[#0A0A0A] border-[#B8860B]"
                        : "border-black/10 hover:border-[#B8860B]"
                    }`}
                    data-testid={`satisfaction-${k}`}
                  >
                    {s.label}
                  </button>
                ))}
                {selected.satisfaction && (
                  <button
                    onClick={() => setSatisfaction(selected, "none")}
                    className="px-3 py-2 text-xs border border-black/10 hover:border-red-500/40 text-[#6B6B6B] hover:text-red-400"
                  >
                    Effacer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-6"
          data-testid="delete-order-modal"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div className="max-w-md w-full bg-white border border-red-500 p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl text-red-700">Supprimer cette commande ?</h3>
            <p className="text-sm text-[#1A1A1A]/85 mt-3">
              Commande <strong className="text-[#B8860B]">{deleteTarget.order_number}</strong> — {deleteTarget.customer_name}.
            </p>
            <p className="text-xs text-[#6B6B6B] mt-2 italic">
              Le stock des articles sera restitué automatiquement si la commande n'était pas déjà « Non livré ». Cette action est irréversible — saisissez votre mot de passe administrateur pour confirmer.
            </p>
            <label className="label-tiny block mt-6 mb-1">Mot de passe administrateur</label>
            <input
              type="password"
              className="input-gold"
              value={deletePwd}
              onChange={(e) => setDeletePwd(e.target.value)}
              autoFocus
              data-testid="delete-order-password"
            />
            <div className="mt-6 flex gap-3">
              <button
                className="btn-secondary flex-1"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                className="btn-primary flex-1 !bg-red-700 !border-red-700 !text-white"
                onClick={confirmDelete}
                disabled={deleting || !deletePwd}
                data-testid="delete-order-confirm"
              >
                {deleting ? "Suppression..." : "Supprimer définitivement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
